import { Fragment, type ReactNode } from 'react';
import { type TextProps, XStack } from 'tamagui';

import { isEmpty } from '../../misc';
import { BadgeLabel, type BadgeLabelProps } from '../badges/Badge';
import { Icon, type IconType } from '../icons/Icon';
import { Private } from '../security/Private';
import { BodyMediumEmphasized, BodySmallRegular } from '../text/Typography';
import { type BaseRowProps, Row } from './Row';

export interface InfoRowProps extends BaseRowProps {
    size?: 'default' | 'large';
    badges?: BadgeLabelProps[];
    value?: string | null | ReactNode;
    valueProps?: TextProps;
    trailingIcon?: IconType;
    isPrivate?: boolean;
}

export const InfoRow = ({
    size = 'default',
    title,
    badges,
    value,
    valueProps,
    trailingIcon,
    isPrivate = false,
    titleProps,
    containerProps,
    ...rowProps
}: InfoRowProps) => {
    const ValueWrapperComponent = isPrivate ? Private : Fragment;

    // Determine title props based on type and size
    const resolvedTitleProps = {
        color: '$gray1000',
        // When size is default, use BodySmallRegular size ($3) instead of BodyMediumRegular size ($4)
        // Explicitly set fontWeight to $1 to ensure regular weight is maintained
        ...(size === 'default' ? { size: '$3', fontWeight: '$1' } : {}),
        ...titleProps,
    };

    // Determine value component based on size
    const ValueComponent =
        size === 'large' ? BodyMediumEmphasized : BodySmallRegular;

    const rightContent = (
        <XStack alignItems="center" gap={8}>
            {/* Badges */}
            {!isEmpty(badges) && (
                <XStack alignItems="center" gap={4}>
                    {badges?.map((badge, index) => (
                        <BadgeLabel key={index} {...badge} />
                    ))}
                </XStack>
            )}

            {/* Value */}
            {value != null && (
                <ValueWrapperComponent>
                    {typeof value === 'string' ? (
                        <ValueComponent
                            maxWidth={200}
                            textAlign="right"
                            {...valueProps}
                        >
                            {value}
                        </ValueComponent>
                    ) : (
                        value
                    )}
                </ValueWrapperComponent>
            )}

            {/* Trailing Icon */}
            {trailingIcon != null && (
                <Icon color="$black" icon={trailingIcon} size={16} />
            )}
        </XStack>
    );

    return (
        <Row
            title={title}
            titleProps={resolvedTitleProps}
            rightContent={rightContent}
            containerProps={{
                backgroundColor: '$gray0',
                ...containerProps,
            }}
            {...rowProps}
        />
    );
};
